import React, { useEffect } from 'react'

import { Grid } from '@mui/material'
import {
  ArrayField,
  AutocompleteInput,
  BooleanInput,
  Datagrid,
  EditButton,
  FunctionField,
  List,
  RaRecord,
  ReferenceField,
  ReferenceInput,
  SearchInput,
  Show,
  ShowButton,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useDataProvider,
  useTranslate,
} from 'react-admin'
import { useParams } from 'react-router-dom'

import { Tooltip } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { LXShowPeriodicRates } from '../../components/LXShowPeriodicRates'
import { ShowPrepayments } from '../../components/Prepayments/ShowPrepayments'
import { API } from '../../helpers/Api.helper'
import { DateHelper } from '../../helpers/Date.helper'
import { ListHelper } from '../../helpers/List.helper'
import { useUserStore } from '../../stores/useUserStore'
import { Prepayment } from '../../types/Prepayments'
import { LXField } from './../../components/LXField'
import { ProductArtworksType } from './../../types/Product'

export const ProductShow = (): JSX.Element => {
  const [recordName, setRecordName] = React.useState('')
  const translate = useTranslate()
  const dataProvider = useDataProvider()
  const params = useParams()
  const userStore = useUserStore()

  const prepaymentsQuery = useQuery<Prepayment[]>({
    queryKey: ['prepayments', params.id],
    queryFn: async () => {
      const response = await API.get<Prepayment[]>('/api/prepayments', {
        params: {
          targetType: 'product',
          targetId: params.id,
        },
      })

      return response.data
    },
    enabled: !!params.id,
  })

  // Get beneficiaryName for App title
  useEffect(() => {
    if (!params?.id) {
      return
    }

    dataProvider
      .getOne('products', { id: params.id as string })
      .then(res => setRecordName(res?.data.name ?? ''))
  }, [dataProvider, params?.id])

  const ProductsShowActions = () => (
    <TopToolbar
      style={{
        paddingBottom: 0,
      }}
    >
      <Tooltip
        label={`⚠ ${translate('pages.users.lackingPermission')} → ${translate(
          'pages.users.permissionsList.products.edit',
        )}`}
        hidden={userStore.isAuthorised('products.edit')}
      >
        <span>
          <EditButton disabled={!userStore.isAuthorised('products.edit')} />
        </span>
      </Tooltip>
    </TopToolbar>
  )

  return (
    <Show
      title={`${translate('pages.products._title')} / ${recordName}`}
      actions={<ProductsShowActions />}
    >
      <TabbedShowLayout>
        <Tab label={translate('pages.products._tabs.1')}>
          <ProductInformations />
        </Tab>
        {userStore.isAuthorised('artworks.read') && (
          <Tab label={translate('pages.products._tabs.2')} path="artworks">
            <ProductArtworks productId={params.id as string} />
          </Tab>
        )}
        {userStore.isAuthorised('statements.read') && (
          <Tab label={translate('pages.products._tabs.3')} path="periodic-rates">
            <LXShowPeriodicRates rateable="product" rateableId={params.id as string} />
          </Tab>
        )}
        {(userStore.isAuthorised('products.edit') || (prepaymentsQuery.data?.length ?? 0) > 0) && (
          <Tab label={translate('pages.products._tabs.4')} path="prepayments">
            <ShowPrepayments targetType="product" targetId={params.id as string} />
          </Tab>
        )}
      </TabbedShowLayout>
    </Show>
  )
}

const ProductInformations = () => {
  const translate = useTranslate()

  return (
    <Grid container direction="row" spacing={2}>
      <Grid container item direction="row" lg={12} spacing={2}>
        <LXField
          source="name"
          label={translate('_generics.name')}
          elementType={TextField}
          size={6}
          labeled
        />
        <LXField
          source="name"
          label={translate('pages.products.productType')}
          elementType={TextField}
          size={6}
          wrappingFunction={el => (
            <ReferenceField source="productTypeId" reference="productTypes" link={false}>
              {el}
            </ReferenceField>
          )}
          labeled
        />
        <LXField
          source="name"
          label={translate('pages.products.beneficiary')}
          elementType={TextField}
          size={6}
          wrappingFunction={el => (
            <ReferenceField source="beneficiaryId" reference="beneficiaries" link="show">
              {el}
            </ReferenceField>
          )}
          labeled
        />
        <LXField
          source="rate"
          label={translate('pages.products.specificRate')}
          elementType={TextField}
          labeled
        />
      </Grid>
    </Grid>
  )
}

export const ProductArtworks = ({ productId }: ProductArtworksType): JSX.Element => {
  const translate = useTranslate()
  const userStore = useUserStore()

  return (
    <List
      resource="artworks"
      filter={{ productId }}
      exporter={false}
      perPage={API.DEFAULT_PAGINATION.perPage}
      sort={{ field: 'createdAt', order: 'DESC' }}
      hasCreate={userStore.isAuthorised('products.edit')}
      filterDefaultValues={{ withoutEmptyAssets: true }}
      filters={[
        <SearchInput key="search" source="q" alwaysOn />,
        <SearchInput
          key="originalArtworkCode"
          source="originalArtworkCode"
          alwaysOn
          placeholder={translate('pages.artworks.artworkCode')}
        />,
        <ReferenceInput
          source="youtubeAssetChannelId"
          reference="youtubeChannels"
          key="filterYT"
          alwaysOn
          allowEmpty={false}
          sort={ListHelper.DEFAULT_SORT}
        >
          <AutocompleteInput optionText="name" label={translate('_generics.youtubeChannel')} />
        </ReferenceInput>,
        <BooleanInput
          key="withoutEmptyAssets"
          source="withoutEmptyAssets"
          label={translate('pages.artworks.withoutEmptyAssets')}
          alwaysOn
        />,
      ]}
    >
      <Datagrid bulkActionButtons={false}>
        <TextField source="name" label={translate('_generics.name')} />
        <TextField source="originalArtworkCode" label={translate('pages.artworks.artworkCode')} />
        <ArrayField source="youtubeAssets" label={translate('_generics.youtubeChannel')}>
          <ReferenceField source="channelId" reference="youtubeChannels" link="show">
            <TextField source="name" />
          </ReferenceField>
        </ArrayField>
        <FunctionField
          source="youtubeAssets"
          label={translate('_generics.youtubeAssets')}
          sortable={false}
          render={(record: RaRecord | undefined) => <span>{record?.youtubeAssets.length}</span>}
        />
        <FunctionField
          source="createdAt"
          label={translate('_generics.createdAt')}
          render={(record: RaRecord | undefined) => (
            <span>{DateHelper.format(record?.createdAt)}</span>
          )}
        />
        <ShowButton />
      </Datagrid>
    </List>
  )
}
